import Phaser from "phaser";
import {
  ModalLayer,
  NineSlicePanel,
  NineSliceButton,
  NINE_SLICE_BUTTON_EVENT,
  SegmentLevelBar,
  SEGMENT_LEVEL_BAR_EVENT,
  computeRowLayout,
} from "@yjsgames/phaser-ui";
import { SOUND_CATEGORIES } from "@yjsgames/sound";
import { CONTENT_PANEL_STYLE, FONT_FAMILY, PRIMARY_BUTTON_STYLE, TYPO } from "./uiTokens.js";
import { t, getTextDirection, type TextDirection } from "../i18n/i18n.js";
import { SUPPORTED_LANGUAGES, LANGUAGE_NAMES, type LanguageCode } from "../i18n/locales.js";
import { Dropdown } from "./Dropdown.js";
import { MAX_VOLUME_LEVEL } from "../models/BrickSettings.js";
import type { NumberFormat } from "../models/ModuleBrickSaveData.js";
import { TARGET_FPS_OPTIONS, type TargetFpsSetting } from "@yjsgames/perf";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { clearCrazyGamesBannerSlot } from "../ads/CrazyGamesBannerSlot.js";
import {
  mountFixedInlineBannerSlot,
  shouldMountCrazyGamesInlineBannerAtRuntime,
} from "./inlineBannerPlaceholder.js";
import { createAccountWarningBadge } from "./accountWarningBadge.js";
import {
  NUMBER_FORMAT_OPTIONS,
  SETTINGS_ACCOUNT_BTN,
  SETTINGS_BANNER_SLOT,
  SETTINGS_DEV_BTN_STEP,
  SETTINGS_INLINE_BANNER_VERTICAL_OFFSET,
  SETTINGS_LABEL_ICON_SIZE,
  SETTINGS_PANEL_WIDTH,
  SOUND_LABEL_KEY,
  VOLUME_BAR_STYLE,
  settingsPanelHeight,
  type SettingsModalOptions,
} from "./settingsModalConfig.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";

export {
  SETTINGS_ACCOUNT_BTN,
  SETTINGS_BANNER_SLOT,
  SETTINGS_PANEL_WIDTH,
  SETTINGS_PANEL_HEIGHT_DEV,
  SETTINGS_PANEL_HEIGHT_LIVE,
  settingsAccountBtnCenterY,
  settingsPanelHeight,
} from "./settingsModalConfig.js";
export type { SettingsModalOptions } from "./settingsModalConfig.js";

export class SettingsModal extends ModalLayer {
  private readonly title: Phaser.GameObjects.Text;
  private readonly langLabel: Phaser.GameObjects.Text;
  private readonly langDropdown: Dropdown<LanguageCode>;
  private readonly formatLabel: Phaser.GameObjects.Text;
  private readonly formatDropdown: Dropdown<NumberFormat>;
  private readonly fpsLabel: Phaser.GameObjects.Text;
  // Dropdown<T extends string> 제약 — TargetFpsSetting(숫자 포함)은 string 키로 다루고 onSelect에서 역변환.
  private readonly fpsDropdown: Dropdown<string>;
  private readonly soundSectionTitle: Phaser.GameObjects.Text;
  private readonly soundLabels: Phaser.GameObjects.Text[] = [];
  private readonly soundBars: SegmentLevelBar[] = [];
  private readonly soundValueLabels: Phaser.GameObjects.Text[] = [];
  // 사운드 행 RTL 재배치용.
  private soundRowLeft = 0;
  private soundLabelW = 0;
  private soundBarW = 0;
  private soundValueW = 0;
  private soundRowGap = 0;
  private readonly accountBtn: NineSliceButton;
  private readonly accountWarningBadge: Phaser.GameObjects.Container;
  private readonly accountWarningText: Phaser.GameObjects.Text;
  private readonly contactBtn: NineSliceButton;
  private readonly otherGamesBtn: NineSliceButton;
  private readonly cheatBtn?: NineSliceButton;
  private readonly debugDashboardBtn?: NineSliceButton;
  private readonly ver: Phaser.GameObjects.Text;
  private readonly version: string;

  constructor(scene: Phaser.Scene, opts: SettingsModalOptions) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.82,
      onBackdropClick: () => {
        if (this.langDropdown.isExpanded || this.formatDropdown.isExpanded || this.fpsDropdown.isExpanded)
          return;
        opts.onClose();
      },
    });

    this.version = opts.version;

    const cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    const pw = SETTINGS_PANEL_WIDTH;
    const inlineBannerEnabled = shouldMountCrazyGamesInlineBannerAtRuntime();
    const inlineOffset = inlineBannerEnabled ? 0 : SETTINGS_INLINE_BANNER_VERTICAL_OFFSET;
    const ph = settingsPanelHeight(opts.cheatAvailable, inlineBannerEnabled);
    const top = cy - ph / 2;
    const labelStyle = { fontFamily: FONT_FAMILY, fontSize: TYPO.body, color: PALETTE.textDim } as const;
    const labelLeft = cx - 100;
    const labelIconX = labelLeft + SETTINGS_LABEL_ICON_SIZE / 2;
    const labelTextX = labelLeft + SETTINGS_LABEL_ICON_SIZE + 6;
    this.setBackdropCloseExclusion(cx - pw / 2, top, pw, ph);

    const panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: pw,
      height: ph,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);

    this.title = scene.add
      .text(cx, top + 26, t("settings.title"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.title,
        color: PALETTE.textPrimary,
      })
      .setOrigin(0.5);
    this.contentContainer.add(this.title);

    const closeBtn = scene.add
      .text(cx + pw / 2 - 18, top + 16, "✕", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => opts.onClose());
    this.contentContainer.add(closeBtn);

    this.accountWarningText = scene.add
      .text(cx, top + 58, t("settings.guestWarning"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.accentGold,
        align: "center",
        wordWrap: { width: 252 },
      })
      .setOrigin(0.5);
    this.contentContainer.add(this.accountWarningText);

    this.addSectionIcon(scene, labelIconX, top + 90, "icon_opt_language");
    this.langLabel = scene.add
      .text(labelTextX, top + 90, t("settings.language"), labelStyle)
      .setOrigin(0, 0.5);
    this.contentContainer.add(this.langLabel);
    this.langDropdown = new Dropdown<LanguageCode>(scene, cx, top + 114, {
      width: 200,
      options: SUPPORTED_LANGUAGES,
      current: opts.language,
      labelOf: (code) => LANGUAGE_NAMES[code],
      onSelect: (code) => opts.onSelectLanguage(code),
      dir: getTextDirection(),
    });
    this.contentContainer.add(this.langDropdown);

    this.addSectionIcon(scene, labelIconX, top + 148, "icon_opt_format");
    this.formatLabel = scene.add
      .text(labelTextX, top + 148, t("settings.numberFormat"), labelStyle)
      .setOrigin(0, 0.5);
    this.contentContainer.add(this.formatLabel);
    this.formatDropdown = new Dropdown<NumberFormat>(scene, cx, top + 172, {
      width: 200,
      options: NUMBER_FORMAT_OPTIONS,
      current: opts.numberFormat,
      labelOf: (f) => t("settings.numberFormat." + f),
      onSelect: (f) => opts.onSelectNumberFormat(f),
      dir: getTextDirection(),
    });
    this.contentContainer.add(this.formatDropdown);

    this.addSectionIcon(scene, labelIconX, top + 206, "icon_opt_fps");
    this.fpsLabel = scene.add
      .text(labelTextX, top + 206, t("settings.targetFps"), labelStyle)
      .setOrigin(0, 0.5);
    this.contentContainer.add(this.fpsLabel);
    this.fpsDropdown = new Dropdown<string>(scene, cx, top + 230, {
      width: 200,
      options: TARGET_FPS_OPTIONS.map(String),
      current: String(opts.targetFps),
      labelOf: (s) => (s === "auto" ? t("settings.targetFps.auto") : s),
      onSelect: (s) =>
        opts.onSelectTargetFps(s === "auto" ? "auto" : (Number(s) as TargetFpsSetting)),
      dir: getTextDirection(),
    });
    this.contentContainer.add(this.fpsDropdown);

    mountFixedInlineBannerSlot(
      scene,
      this.contentContainer,
      "settings",
      {
        x: cx - SETTINGS_BANNER_SLOT.width / 2,
        y: top + SETTINGS_BANNER_SLOT.topOffset,
        width: SETTINGS_BANNER_SLOT.width,
        height: SETTINGS_BANNER_SLOT.height,
      },
      inlineBannerEnabled,
    );

    this.addSectionIcon(scene, labelIconX, top + 316 - inlineOffset, "icon_opt_sound");
    this.soundSectionTitle = scene.add
      .text(labelTextX, top + 316 - inlineOffset, t("settings.sound"), labelStyle)
      .setOrigin(0, 0.5);
    this.contentContainer.add(this.soundSectionTitle);

    const dir = getTextDirection();
    this.soundLabelW = 50;
    this.soundBarW = 140;
    this.soundValueW = 22;
    this.soundRowGap = 8;
    this.soundRowLeft =
      cx - (this.soundLabelW + this.soundBarW + this.soundValueW + this.soundRowGap * 2) / 2;
    const xs = this.soundRowXs(dir);
    const barH = 18;
    const rowH = 34;
    const soundTop = top + 346 - inlineOffset;
    SOUND_CATEGORIES.forEach((cat, i) => {
      const rowY = soundTop + i * rowH;
      const label = scene.add
        .text(this.soundRowLeft + (xs[0] ?? 0), rowY, t(SOUND_LABEL_KEY[cat]), labelStyle)
        .setOrigin(0, 0.5)
        .setInteractive(
          new Phaser.Geom.Rectangle(0, 0, this.soundLabelW, 24),
          Phaser.Geom.Rectangle.Contains,
        );
      label.input!.cursor = "pointer";
      this.contentContainer.add(label);
      this.soundLabels.push(label);

      const bar = new SegmentLevelBar(scene, this.soundRowLeft + (xs[1] ?? 0), rowY - barH / 2, {
        style: VOLUME_BAR_STYLE,
        width: this.soundBarW,
        height: barH,
        maxLevel: MAX_VOLUME_LEVEL,
        level: opts.soundVolumes[cat],
        dir,
      });
      this.contentContainer.add(bar);
      this.soundBars.push(bar);

      const valueLabel = scene.add
        .text(
          this.soundRowLeft + (xs[2] ?? 0) + this.soundValueW / 2,
          rowY,
          String(opts.soundVolumes[cat]),
          { fontFamily: FONT_FAMILY, fontSize: TYPO.body, color: PALETTE.textPrimary },
        )
        .setOrigin(0.5, 0.5)
        .setInteractive(
          new Phaser.Geom.Rectangle(0, 0, this.soundValueW, 24),
          Phaser.Geom.Rectangle.Contains,
        );
      valueLabel.input!.cursor = "pointer";
      this.contentContainer.add(valueLabel);
      this.soundValueLabels.push(valueLabel);

      const setShortcutLevel = (level: number): void => {
        if (bar.getLevel() === level) return;
        bar.setLevel(level);
        valueLabel.setText(String(level));
        opts.onChangeSoundVolume(cat, level);
      };
      label.on("pointerup", () => setShortcutLevel(0));
      valueLabel.on("pointerup", () => setShortcutLevel(MAX_VOLUME_LEVEL));
      bar.on(SEGMENT_LEVEL_BAR_EVENT.Change, (lv: number) => {
        valueLabel.setText(String(lv));
        opts.onChangeSoundVolume(cat, lv);
      });
    });

    const accountOffset = SETTINGS_ACCOUNT_BTN.topOffset - inlineOffset;
    this.accountBtn = new NineSliceButton(scene, cx, top + accountOffset, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("settings.accountManage"),
      iconKey: "icon_account_link",
      widthOverride: SETTINGS_ACCOUNT_BTN.width,
      heightOverride: SETTINGS_ACCOUNT_BTN.height,
    });
    this.accountBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onOpenAccount());
    this.contentContainer.add(this.accountBtn);
    this.accountWarningBadge = createAccountWarningBadge(
      scene,
      cx + SETTINGS_ACCOUNT_BTN.width / 2 - 6,
      top + accountOffset - SETTINGS_ACCOUNT_BTN.height / 2 + 2,
    );
    this.contentContainer.add(this.accountWarningBadge);
    this.setAccountNeedsLink(opts.accountNeedsLink ?? false);

    this.contactBtn = new NineSliceButton(scene, cx, top + accountOffset + SETTINGS_DEV_BTN_STEP, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("settings.contact"),
      iconKey: "icon_contact",
      widthOverride: SETTINGS_ACCOUNT_BTN.width,
      heightOverride: SETTINGS_ACCOUNT_BTN.height,
    });
    this.contactBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onOpenContact());
    this.contentContainer.add(this.contactBtn);

    this.otherGamesBtn = new NineSliceButton(scene, cx, top + accountOffset + SETTINGS_DEV_BTN_STEP * 2, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("settings.otherGames"),
      iconKey: "icon_other_games",
      widthOverride: SETTINGS_ACCOUNT_BTN.width,
      heightOverride: SETTINGS_ACCOUNT_BTN.height,
    });
    this.otherGamesBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onOpenOtherGames());
    this.contentContainer.add(this.otherGamesBtn);

    if (opts.cheatAvailable) {
      this.debugDashboardBtn = new NineSliceButton(scene, cx, top + accountOffset + SETTINGS_DEV_BTN_STEP * 3, {
        style: PRIMARY_BUTTON_STYLE,
        label: "QA 대시보드",
        widthOverride: 200,
        heightOverride: 38,
      });
      this.debugDashboardBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onOpenDebugDashboard());
      this.contentContainer.add(this.debugDashboardBtn);

      this.cheatBtn = new NineSliceButton(scene, cx, top + accountOffset + SETTINGS_DEV_BTN_STEP * 4, {
        style: PRIMARY_BUTTON_STYLE,
        label: "치트 (DEV)",
        widthOverride: 200,
        heightOverride: 38,
      });
      this.cheatBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onOpenCheat());
      this.contentContainer.add(this.cheatBtn);
    }

    this.ver = scene.add
      .text(cx + pw / 2 - 12, top + ph - 12, t("settings.version", { version: opts.version }), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textDim,
      })
      .setOrigin(1, 1);
    this.contentContainer.add(this.ver);
  }

  private addSectionIcon(scene: Phaser.Scene, x: number, y: number, key: string): void {
    const icon = scene.add
      .image(x, y, key)
      .setDisplaySize(SETTINGS_LABEL_ICON_SIZE, SETTINGS_LABEL_ICON_SIZE);
    icon.setTint(PALETTE_NUM.textDim);
    this.contentContainer.add(icon);
  }

  private soundRowXs(dir: TextDirection): number[] {
    return computeRowLayout(
      [{ width: this.soundLabelW }, { width: this.soundBarW }, { width: this.soundValueW }],
      this.soundRowGap,
      dir,
    ).xs;
  }

  setNumberFormat(format: NumberFormat): void {
    this.formatDropdown.setCurrent(format);
  }

  setAccountNeedsLink(value: boolean): void {
    this.accountWarningBadge.setVisible(value);
    this.accountWarningText.setVisible(value);
  }

  applyLanguage(code: LanguageCode): void {
    this.title.setText(t("settings.title"));
    this.langLabel.setText(t("settings.language"));
    this.formatLabel.setText(t("settings.numberFormat"));
    this.fpsLabel.setText(t("settings.targetFps"));
    this.soundSectionTitle.setText(t("settings.sound"));
    SOUND_CATEGORIES.forEach((cat, i) => this.soundLabels[i]?.setText(t(SOUND_LABEL_KEY[cat])));
    this.accountBtn.setLabel(t("settings.accountManage"));
    this.accountWarningText.setText(t("settings.guestWarning"));
    this.contactBtn.setLabel(t("settings.contact"));
    this.otherGamesBtn.setLabel(t("settings.otherGames"));
    this.ver.setText(t("settings.version", { version: this.version }));
    this.langDropdown.setCurrent(code);
    this.formatDropdown.refreshLabels();
    this.fpsDropdown.refreshLabels();

    const dir = getTextDirection(code);
    this.langDropdown.setDirection(dir);
    this.formatDropdown.setDirection(dir);
    this.fpsDropdown.setDirection(dir);
    const xs = this.soundRowXs(dir);
    this.soundLabels.forEach((l) => l.setX(this.soundRowLeft + (xs[0] ?? 0)));
    this.soundBars.forEach((b) => {
      b.setX(this.soundRowLeft + (xs[1] ?? 0));
      b.setDirection(dir);
    });
    this.soundValueLabels.forEach((v) =>
      v.setX(this.soundRowLeft + (xs[2] ?? 0) + this.soundValueW / 2),
    );
  }

  preDestroy(): void {
    clearCrazyGamesBannerSlot("settings");
  }
}
